<script>
  import tipoUsuario from '~js/tipo-usuario';

  // Driver.js é carregado via _Layout.cshtml (IIFE em /lib/driver-js/driver.js)
  // porque o build do projeto (Webpack 2 + Babel 6) não transpila ES2020 em node_modules.
  // A função fica em window.driver.js.driver. CSS é carregado em paralelo.
  function getDriver() {
    if (typeof window === 'undefined') return null;
    if (window.driver && window.driver.js && typeof window.driver.js.driver === 'function') {
      return window.driver.js.driver;
    }
    return null;
  }

  // Chave usada para persistir o estado do tour entre navegações Vue Router
  // (quando um step do tipo `navigate` muda a rota).
  const TOUR_RESUME_KEY = 'isaac_tour_resume';
  const TOUR_MUTED_KEY = 'isaac_tour_muted';

  // Lista ranqueada de preferências de voz para TTS pt-BR. Match por substring
  // case-insensitive contra `voice.name`. Mais natural primeiro. Top-level
  // (const, não data) porque é estático e shared entre todas as instâncias.
  const TTS_VOICE_PREFERENCE = [
    'google português do brasil',
    'google portugues do brasil',
    'google pt-br',
    'francisca online',
    'antonio online',
    'heloisa online',
    'maria online',
    'thalita online',
    'francisca',
    'antonio',
    'heloisa',
    'maria',
    'luciana',
  ];

  export default {
    name: 'ChatBotWidget',
    data() {
      return {
        isOpen: false,
        iframeLoaded: false,
        usuario: this.$storage.usuario(),
        configuracao: this.$storage.configuracao(),
        // Tour state
        tourActive: false,
        tourMuted: localStorage.getItem(TOUR_MUTED_KEY) === '1',
      }
    },
    computed: {
      isaacHubUrl() {
        // CamelCasePropertyNamesContractResolver do .NET converte para camelCase no JSON
        return this.configuracao && this.configuracao.urlIsaacHubFundacaoWeb
          ? this.configuracao.urlIsaacHubFundacaoWeb.replace(/\/+$/, '')
          : '';
      },
      isaacOrigin() {
        if (!this.isaacHubUrl) return '';
        try {
          return new URL(this.isaacHubUrl).origin;
        } catch (_) {
          return '';
        }
      },
      isVisible() {
        return !!(
          this.isaacHubUrl &&
          this.usuario &&
          this.usuario.tipoUsuario === tipoUsuario.Coordenador
        );
      }
    },
    watch: {
      // Após $router.push (step `navigate`), o ChatBotWidget continua montado
      // no Layout — `mounted()` não roda de novo. O watcher do `$route` força
      // a verificação de tour pendente em cada navegação.
      '$route'() {
        this.tryResumePendingTour();
      },
    },
    mounted() {
      // Injeta CSS específico do tour: durante o tour (body.driver-active),
      // a sidebar vira um painel FIXO de altura total com scroll próprio. Isso
      // permite rolar até itens de menu abaixo da dobra SEM deformar o layout:
      // - position:fixed + height:100vh => o fundo escuro preenche a viewport
      //   inteira (antes, max-height:100vh num elemento position:absolute
      //   recortava o fundo e a sidebar "encolhia").
      // - é o ÚNICO container rolável: os filhos (.sidebar-collapse / #side-menu)
      //   ficam com max-height:none/overflow:visible pra não criar scrollbars
      //   aninhadas que reflowavam o conteúdo.
      // Como a sidebar já é absolute/fixed (fora do fluxo) e o #page-wrapper usa
      // margin-left, fixá-la não desloca o conteúdo central.
      if (!document.getElementById('isaac-tour-css')) {
        const tourStyle = document.createElement('style');
        tourStyle.id = 'isaac-tour-css';
        tourStyle.textContent = [
          'body.driver-active .navbar-static-side {',
          '  position: fixed !important;',
          '  top: 0 !important;',
          '  left: 0 !important;',
          '  height: 100vh !important;',
          '  max-height: 100vh !important;',
          '  overflow-y: auto !important;',
          '  overflow-x: hidden !important;',
          '}',
          'body.driver-active .sidebar-collapse,',
          'body.driver-active #side-menu {',
          '  max-height: none !important;',
          '  overflow: visible !important;',
          '}',
        ].join('\n');
        document.head.appendChild(tourStyle);
      }

      // Listener postMessage do iframe do Isaac (chat embedado).
      this._messageHandler = this.onIsaacMessage.bind(this);
      window.addEventListener('message', this._messageHandler);

      // Retoma tour pendente após navegação (step do tipo `navigate`).
      this.tryResumePendingTour();

      // Modo preview: o configurador clicou em "Pré-visualizar" no Isaac e a
      // aba foi aberta com `?isaac_preview=<token>&isaac_origin=<origin>`.
      // Token é descartável (5 min, single-use intencional) — limpa a URL após
      // consumir pra que um F5 não tente reusar token vencido.
      this.tryStartIsaacPreview();

      // Pre-carrega vozes do TTS. No Chrome a lista chega async via evento
      // `voiceschanged` — sem isso o 1o speak() pega voz default robotica e so
      // depois cachearia a melhor. Forca uma chamada inicial pra disparar o
      // load e escuta o evento pra recachear quando a lista atualizar.
      if ('speechSynthesis' in window) {
        try { window.speechSynthesis.getVoices(); } catch (_) {}
        this._voicesChangedHandler = () => {
          this._chosenVoice = this._pickBestPtBrVoice();
        };
        window.speechSynthesis.addEventListener('voiceschanged', this._voicesChangedHandler);
        // Caso voiceschanged ja tenha disparado antes do listener, tenta agora.
        this._chosenVoice = this._pickBestPtBrVoice();
      }
    },
    beforeDestroy() {
      if (this._messageHandler) {
        window.removeEventListener('message', this._messageHandler);
      }
      if (this._voicesChangedHandler && 'speechSynthesis' in window) {
        try {
          window.speechSynthesis.removeEventListener('voiceschanged', this._voicesChangedHandler);
        } catch (_) {}
      }
      this.stopTour();
    },
    methods: {
      toggle() {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
          this.iframeLoaded = true;
        }
      },
      close() {
        this.isOpen = false;
      },

      // ===== Mensagens vindas do iframe do Isaac =====
      onIsaacMessage(event) {
        // Filtra mensagens que não são do Isaac.
        if (!this.isaacOrigin || event.origin !== this.isaacOrigin) return;
        const iframe = this.$refs.isaacIframe;
        if (iframe && event.source !== iframe.contentWindow) return;

        const data = event.data || {};
        if (!data || data.source !== 'isaac-hub') return;

        if (data.type === 'isaac.tour.start') {
          this.startTour(data.payload || {});
        } else if (data.type === 'isaac.tour.stop') {
          this.stopTour();
        } else if (data.type === 'isaac.tour.toggle_mute') {
          this.toggleMute();
        }
      },

      // ===== Inicia ou retoma o tour =====
      async startTour(payload) {
        const { tourSlug, apiBase, auth, isaacOrigin: origin, _resumeIndex } = payload;
        if (!tourSlug || !apiBase) {
          console.warn('[isaac-tour] payload inválido', payload);
          return;
        }

        // [CHATBOT-WIDGET] Defesa contra estados de fala remanescentes de um tour
        // anterior (utterances de speechSynthesis enfileiradas pelo browser ou
        // HTMLAudioElement ainda tocando).
        this.stopSpeak();

        // Recolhe o widget para o tour ter espaço livre.
        this.tourActive = true;
        this.isOpen = false;

        let stepsData;
        try {
          stepsData = await this.fetchTourSteps({ tourSlug, apiBase, auth, isaacOrigin: origin });
        } catch (err) {
          console.error('[isaac-tour] erro ao carregar tour', err);
          this.tourActive = false;
          return;
        }
        if (!stepsData || !stepsData.steps || stepsData.steps.length === 0) {
          console.warn('[isaac-tour] tour sem passos');
          this.tourActive = false;
          return;
        }

        // Guarda contexto do tour para fetch da telemetria + retomada via navigate.
        this._tourCtx = {
          tourSlug,
          apiBase,
          auth,
          isaacOrigin: origin || this.isaacOrigin,
          stepsData,
        };

        const startIndex = (typeof _resumeIndex === 'number' && _resumeIndex > 0)
          ? Math.min(_resumeIndex, stepsData.steps.length - 1)
          : 0;

        // [CHATBOT-WIDGET] Toca o "objetivo" ANTES de iniciar o Driver — senão o
        // callback `onHighlightStarted` do step 0 dispara em paralelo e os dois
        // áudios sobrepõem (audio.play() é assíncrono e o pause() do _stopCurrentAudio
        // não atinge a Promise pendente em todos os browsers).
        if (startIndex === 0 && (stepsData.objetivo || stepsData.audio_url) && !this.tourMuted) {
          await this.speak(stepsData.audio_url, stepsData.objetivo);
        }

        this.runDriver(stepsData, startIndex);

        if (startIndex === 0) {
          this.reportEvent('started', 0);
        }
      },

      async fetchTourSteps({ tourSlug, apiBase, auth, isaacOrigin }) {
        const origin = isaacOrigin || this.isaacOrigin;
        const url = origin + apiBase + tourSlug + '/';
        const fetchOpts = {
          method: 'GET',
          credentials: (auth && auth.mode === 'session') ? 'include' : 'omit',
          headers: { 'Accept': 'application/json' },
        };
        const resp = await fetch(url, fetchOpts);
        if (!resp.ok) {
          throw new Error('HTTP ' + resp.status);
        }
        return await resp.json();
      },

      runDriver(stepsData, startIndex) {
        const vm = this;
        const driverFn = getDriver();
        if (!driverFn) {
          console.error('[isaac-tour] driver.js não carregado (window.driver.js ausente). Verifique /lib/driver-js/driver.js no _Layout.');
          this.tourActive = false;
          return;
        }
        const driverSteps = stepsData.steps.map(step => vm.toDriverStep(step));

        const drv = driverFn({
          showProgress: true,
          animate: true,
          allowClose: true,
          stagePadding: 6,
          smoothScroll: true,
          nextBtnText: 'Próximo &rarr;',
          prevBtnText: '&larr; Anterior',
          doneBtnText: 'Concluir',
          progressText: '{{current}} de {{total}}',
          onCloseClick: () => {
            vm.reportEvent('skipped', drv.getActiveIndex());
            drv.destroy();
          },
          onPopoverRender: (popover, opts) => {
            // Botão de mute na barra de controle do popover.
            try {
              const muteBtn = document.createElement('button');
              muteBtn.type = 'button';
              muteBtn.className = 'driver-popover-mute-btn';
              muteBtn.innerHTML = vm.tourMuted ? '🔇' : '🔊';
              muteBtn.title = vm.tourMuted ? 'Ativar narração' : 'Silenciar narração';
              muteBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                vm.toggleMute();
                muteBtn.innerHTML = vm.tourMuted ? '🔇' : '🔊';
                muteBtn.title = vm.tourMuted ? 'Ativar narração' : 'Silenciar narração';
              });
              if (popover.title) {
                popover.title.parentNode.insertBefore(muteBtn, popover.title);
              }
            } catch (_) { /* noop */ }
          },
          onHighlightStarted: (el, step, opts) => {
            const raw = stepsData.steps[opts.state.activeIndex];
            if (!raw) return;
            // Scroll antecipado: garante que o elemento destacado fique visível
            // mesmo quando estiver fora da viewport (ex: item de submenu da sidebar
            // que precisa de scroll vertical pra aparecer). Usa _scrollIntoViewDeep
            // pra rolar containers internos (sidebar com overflow próprio), não só
            // a janela do navegador.
            if (raw.selector) {
              try {
                const tgt = document.querySelector(raw.selector);
                vm._scrollIntoViewDeep(tgt);
              } catch (_) {}
            }
            // Narra o texto da fala (ou descrição limpa) ao chegar no passo.
            // Guarda a Promise da fala em _currentSpeakPromise pros waiters
            // sincronizarem o avanço com o fim da narração (modo vídeo).
            // [CHATBOT-WIDGET] Prefere raw.audio_url (TTS pre-renderizado) quando
            // pronto; fallback para speechSynthesis usando o texto da fala.
            const text = raw.texto_fala || vm.stripHtml(raw.descricao || '');
            const hasNarration = raw.audio_url || text;
            vm._currentSpeakPromise = (hasNarration && !vm.tourMuted)
              ? vm.speak(raw.audio_url, text)
              : Promise.resolve();
            // Pre-carrega o audio do proximo step pra evitar gap entre falas.
            try {
              const next = stepsData.steps[opts.state.activeIndex + 1];
              if (next && next.audio_url) {
                const a = new Audio();
                a.preload = 'auto';
                a.src = next.audio_url;
              }
            } catch (_) {}
            // Listeners auxiliares para wait_for / wait_click.
            vm.attachStepWaiters(raw, drv, opts.state.activeIndex);
          },
          onDeselected: () => {
            vm.detachStepWaiters();
            vm.stopSpeak();
          },
          onDestroyed: () => {
            // Aciona ao chegar no fim ou no close.
            const activeIndex = drv.getActiveIndex ? drv.getActiveIndex() : null;
            // Se chegou ao fim normalmente, reporta completed.
            const totalSteps = stepsData.steps.length;
            if (activeIndex !== null && activeIndex >= totalSteps - 1) {
              vm.reportEvent('completed', activeIndex);
            }
            vm.detachStepWaiters();
            vm.stopSpeak();
            // Remove os inline styles que _scrollIntoViewDeep forcou na sidebar
            // pra rolagem interna durante o tour. Sem isso, a sidebar fica fixa
            // com overflow/max-height permanentes, deformando o layout fora do
            // tour. Em navegacao (resume), tambem limpa — a sidebar nova reforca.
            try {
              const sidebar = document.querySelector('.navbar-static-side');
              if (sidebar) {
                ['position', 'top', 'left', 'height', 'max-height', 'overflow-y', 'overflow-x']
                  .forEach(p => sidebar.style.removeProperty(p));
              }
              ['.sidebar-collapse', '#side-menu'].forEach(sel => {
                const inner = document.querySelector(sel);
                if (inner) {
                  inner.style.removeProperty('max-height');
                  inner.style.removeProperty('overflow');
                }
              });
            } catch (_) {}
            // Só limpa TOUR_RESUME_KEY se NÃO estamos em meio de navegação.
            // O navigate handler salva o resume no sessionStorage e logo depois
            // chama drv.destroy(), que dispara este callback — sem o flag, o
            // remove abaixo apagaria o resume que acabamos de salvar.
            if (!vm._navigatingToResume) {
              sessionStorage.removeItem(TOUR_RESUME_KEY);
            }
            vm.tourActive = false;
            // Recarrega a tela ao fim do tour (fora de navegacao) para resetar o
            // estado do menu lateral: os auto_click de expansao de submenu deixam
            // o metisMenu dessincronizado. Delay deixa a telemetria completed sair.
            if (!vm._navigatingToResume) {
              setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 400);
            }
            vm._driverInstance = null;
          },
        });

        // Define os passos e leva ao step inicial.
        drv.setSteps(driverSteps);
        drv.drive(startIndex);

        this._driverInstance = drv;
      },

      // Converte um step da API do Isaac para o formato do Driver.js.
      toDriverStep(raw) {
        const sideMap = { top: 'top', bottom: 'bottom', left: 'left', right: 'right', over: 'over' };
        const side = sideMap[raw.posicao_popover] || 'bottom';
        const isNote = raw.acao === 'note' || !raw.selector;

        const popover = {
          title: raw.titulo || '',
          description: raw.descricao || '',
          side: side,
          align: 'start',
        };

        // Esconde botão "Skip" se permite_pular = false.
        if (raw.permite_pular === false) {
          popover.showButtons = ['next', 'previous', 'close'];
        }

        // Steps do tipo `navigate` ganham um botão "Ir" customizado.
        if (raw.acao === 'navigate' && raw.url_destino) {
          popover.showButtons = ['previous', 'close'];
          popover.popoverClass = 'isaac-tour-navigate';
        }

        if (isNote) {
          return { popover };
        }
        return {
          element: raw.selector,
          popover,
        };
      },

      // Rola a janela E os ancestors scrollable até o elemento ficar visível.
      // Suporta 4 modos: window scroll manual (sempre), slimScroll (InspinIA),
      // overflow CSS padrão e scrollIntoView final. Comportamento INSTANT pra
      // garantir que o scroll aconteça antes do Driver.js renderizar o popover
      // (smooth pode ser interrompido entre múltiplas chamadas).
      _scrollIntoViewDeep(el) {
        if (!el) return;

        // Garantia BRUTA via inline style (specificity máxima — vence qualquer CSS):
        // espelha o isaac-tour-css. A .navbar-static-side vira o ÚNICO container
        // rolável (fixo, altura total) e os filhos liberam a altura. O onDestroyed
        // remove estes properties quando o tour termina, evitando scroll/layout
        // persistente fora do tour.
        try {
          const sidebar = document.querySelector('.navbar-static-side');
          if (sidebar) {
            sidebar.style.setProperty('position', 'fixed', 'important');
            sidebar.style.setProperty('top', '0', 'important');
            sidebar.style.setProperty('left', '0', 'important');
            sidebar.style.setProperty('height', '100vh', 'important');
            sidebar.style.setProperty('max-height', '100vh', 'important');
            sidebar.style.setProperty('overflow-y', 'auto', 'important');
            sidebar.style.setProperty('overflow-x', 'hidden', 'important');
          }
          ['.sidebar-collapse', '#side-menu'].forEach(sel => {
            const inner = document.querySelector(sel);
            if (inner) {
              inner.style.setProperty('max-height', 'none', 'important');
              inner.style.setProperty('overflow', 'visible', 'important');
            }
          });
        } catch (_) {}

        const elRect = el.getBoundingClientRect();

        // Modo 0: percorre TODOS os ancestrais e rola QUALQUER um que tenha
        // scrollHeight > clientHeight (conteúdo overflow). Robusto pois pega
        // tanto containers com overflow CSS auto/scroll quanto casos onde o
        // overflow foi forçado via inline style (sidebar do template InspinIA).
        try {
          let parent = el.parentElement;
          while (parent) {
            if (parent.scrollHeight > parent.clientHeight + 5) {
              const parentRect = parent.getBoundingClientRect();
              const elRectNow = el.getBoundingClientRect();
              const targetTop = Math.max(0,
                parent.scrollTop + (elRectNow.top - parentRect.top) - 80
              );
              try { parent.scrollTo({ top: targetTop, behavior: 'smooth' }); } catch (_) {
                parent.scrollTop = targetTop;
              }
            }
            if (parent === document.documentElement) break;
            parent = parent.parentElement;
          }
          // Também tenta window scroll (cobre o caso onde o scroll é dado ao
          // html/body que não aparecem com scrollHeight > clientHeight).
          const absoluteTop = elRect.top + (window.pageYOffset || window.scrollY || 0);
          const targetY = Math.max(0, absoluteTop - 80);
          try { window.scrollTo({ top: targetY, behavior: 'smooth' }); } catch (_) {
            try { window.scrollTo(0, targetY); } catch (_) {}
          }
        } catch (_) {}

        // Modo 1: slimScroll (jQuery plugin) — InspinIA aplica em .slimScrollDiv
        // quando body tem .fixed-sidebar. O wrapper tem overflow:hidden e o
        // conteúdo é movido via top/transform.
        try {
          const slimWrap = el.closest && el.closest('.slimScrollDiv');
          if (slimWrap) {
            const $ = window.jQuery || window.$;
            const elRect2 = el.getBoundingClientRect();
            const wrapRect = slimWrap.getBoundingClientRect();
            const offsetWithin = (elRect2.top - wrapRect.top) -
                                 (slimWrap.clientHeight / 2) +
                                 (el.offsetHeight / 2);
            if ($ && $.fn && $.fn.slimScroll) {
              const slimContent = slimWrap.firstElementChild;
              if (slimContent) {
                $(slimContent).slimScroll({
                  scrollTo: Math.max(0, slimContent.scrollTop + offsetWithin) + 'px',
                });
              }
            } else {
              slimWrap.scrollTop = Math.max(0, slimWrap.scrollTop + offsetWithin);
            }
          }
        } catch (_) {}

        // Modo 2: ancestors com overflow CSS padrão (auto/scroll).
        let parent = el.parentElement;
        while (parent && parent !== document.documentElement) {
          let overflowY = '';
          try {
            overflowY = window.getComputedStyle(parent).overflowY;
          } catch (_) {}
          if (overflowY === 'auto' || overflowY === 'scroll') {
            const parentRect = parent.getBoundingClientRect();
            const elRect3 = el.getBoundingClientRect();
            const partiallyOff = (elRect3.top < parentRect.top + 8) ||
                                 (elRect3.bottom > parentRect.bottom - 8);
            if (partiallyOff) {
              const desiredTop = parent.scrollTop +
                                 (elRect3.top - parentRect.top) -
                                 (parent.clientHeight / 2) +
                                 (el.offsetHeight / 2);
              parent.scrollTop = Math.max(0, desiredTop);
            }
          }
          parent = parent.parentElement;
        }

        // Modo 3: scrollIntoView final como suspensório (cobre casos exóticos).
        try {
          el.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
        } catch (_) {}
      },

      // ===== Auxiliares de wait_for / wait_click / navigate =====
      attachStepWaiters(raw, drv, index) {
        this.detachStepWaiters();
        const vm = this;

        // wait_click: avança quando o usuário clicar no elemento destacado.
        if (raw.acao === 'wait_click' && raw.selector) {
          const target = document.querySelector(raw.selector);
          if (target) {
            this._clickHandler = () => {
              try { drv.moveNext(); } catch (_) {}
            };
            target.addEventListener('click', this._clickHandler, { once: true });
            this._clickTarget = target;
          }
        }

        // auto_click: aguarda o MAIOR entre (timeout_ms, fim da narração TTS),
        // dispara click() no elemento e avança o tour. Usado em modo "vídeo" para
        // demonstrar fluxos sem interação do usuário (abre menu, troca abas, etc.).
        if (raw.acao === 'auto_click' && raw.selector) {
          const delay = raw.timeout_ms || 2500;
          // Scroll antecipado: garante que itens fora da viewport (ex: item de
          // submenu da sidebar) fiquem visíveis antes do popover aparecer.
          try {
            vm._scrollIntoViewDeep(document.querySelector(raw.selector));
          } catch (_) {}
          // Modo vídeo: só avança quando a narração terminar E o timeout mínimo
          // estourar. Promise.all resolve quando AMBOS resolverem (max das duas).
          const speakPromise = vm._currentSpeakPromise || Promise.resolve();
          const fallbackPromise = new Promise(resolve => {
            this._autoClickTimeout = setTimeout(resolve, delay);
          });
          this._activeWaiterDriver = drv;
          Promise.all([speakPromise, fallbackPromise]).then(() => {
            // Aborta se o tour já mudou de step ou foi destruído.
            if (vm._driverInstance !== drv || this._activeWaiterDriver !== drv) return;
            // Margem confortável após fim da fala antes de clicar e avançar.
            this._postSpeakTimeout = setTimeout(() => {
              if (vm._driverInstance !== drv) return;
              const target = document.querySelector(raw.selector);
              if (target) {
                // Se o target for um <li> de menu, clica no <a> interno — é nele que
                // o metisMenu/router amarra o handler; click() no <li> nem sempre dispara.
                const clickable = (target.tagName === 'A')
                  ? target
                  : (target.querySelector(':scope > a') || target);
                try { clickable.click(); } catch (_) {}
                // Força expansão do submenu colapsado, cobrindo dois layouts:
                //  - submenu como FILHO do próprio target (target é o <li> pai, ex: Compra
                //    com o 3º nível Nacional/Online dentro)
                //  - submenu como irmão via parentElement (target é o <a>, ex: Solicitações)
                // O 3º nível só abre via binding do Vue quando a rota já é a do submenu,
                // então sem forçar aqui o item fica com altura 0 e o popover se perde.
                try {
                  const sub = ':scope > .collapse, :scope > .nav-second-level, :scope > .nav-third-level';
                  const submenu = target.querySelector(sub) ||
                                  (target.parentElement && target.parentElement.querySelector(sub));
                  if (submenu && !submenu.classList.contains('in')) {
                    submenu.classList.add('in');
                    // Reseta height inline (Bootstrap collapse pode ter setado 0).
                    submenu.style.height = 'auto';
                  }
                } catch (_) {}
              }
              // Aguarda animação do submenu antes de avançar (Bootstrap collapse ~350ms).
              // 800ms dá margem extra pra layout reflow + scroll do próximo step.
              setTimeout(() => {
                if (vm._driverInstance !== drv) return;
                try { drv.moveNext(); } catch (_) {}
              }, 800);
            }, 800);
          });
        }

        // wait_for: avança quando o `selector_aguardar` aparecer no DOM,
        // OU quando o MAIOR entre (timeout_ms, fim da narração) estourar.
        if (raw.acao === 'wait_for' && raw.selector_aguardar) {
          const timeoutMs = raw.timeout_ms || 5000;
          let advanced = false;
          const advance = () => {
            if (advanced) return;
            advanced = true;
            vm.detachStepWaiters();
            try { drv.moveNext(); } catch (_) {}
          };
          const tryAdvance = () => {
            const el = document.querySelector(raw.selector_aguardar);
            if (el) { advance(); return true; }
            return false;
          };
          if (!tryAdvance()) {
            this._observer = new MutationObserver(() => { tryAdvance(); });
            this._observer.observe(document.body, { childList: true, subtree: true });
            // Sincronizado com TTS: só avança após a fala terminar E o timeout.
            const speakPromise = vm._currentSpeakPromise || Promise.resolve();
            const fallbackPromise = new Promise(resolve => {
              this._waitTimeout = setTimeout(resolve, timeoutMs);
            });
            Promise.all([speakPromise, fallbackPromise]).then(() => {
              if (vm._driverInstance !== drv) return;
              // Margem confortável após fim da fala antes de avançar.
              this._postSpeakTimeout = setTimeout(() => {
                if (vm._driverInstance !== drv) return;
                advance();
              }, 800);
            });
          }
        }

        // ===== Acoes de cenario controlado (Tour Guiado com dados demo) =====
        // Estas tres acoes leem `tour_context` (definido no Isaac Hub) para preencher
        // o estado da tela sem depender de cliques cegos do usuario.

        // set_project: aplica `tour_context.demo_project_id` no select2 do projeto
        // (assume `#projeto` na tela atual). Funciona para Lancamentos.vue e qualquer
        // outra tela que use o mesmo id.
        if (raw.acao === 'set_project') {
          const ctx = (vm._tourCtx && vm._tourCtx.stepsData && vm._tourCtx.stepsData.tour_context) || {};
          const projectId = ctx.demo_project_id;
          const projectName = ctx.demo_project_name;
          const delay = raw.timeout_ms || 2500;
          if (projectId == null && !projectName) {
            console.warn('[isaac-tour] set_project: tour_context.demo_project_id e demo_project_name ausentes — passo vira no-op');
          }
          const speakPromise = vm._currentSpeakPromise || Promise.resolve();
          const fallbackPromise = new Promise(resolve => {
            this._autoClickTimeout = setTimeout(resolve, delay);
          });
          this._activeWaiterDriver = drv;
          Promise.all([speakPromise, fallbackPromise]).then(() => {
            if (vm._driverInstance !== drv || this._activeWaiterDriver !== drv) return;
            this._postSpeakTimeout = setTimeout(async () => {
              if (vm._driverInstance !== drv) return;
              try { await vm._applyTourSetProject(projectId, projectName); } catch (e) { console.warn('[isaac-tour] set_project falhou', e); }
              if (vm._driverInstance !== drv) return;
              setTimeout(() => {
                if (vm._driverInstance !== drv) return;
                try { drv.moveNext(); } catch (_) {}
              }, 800);
            }, 800);
          });
        }

        // set_filter: aplica datas e (quando aplicavel) marca o checkbox de
        // rendimento liquido. Le `data_inicial`, `data_final` e
        // `mostrar_rendimento_liquido` de `tour_context`.
        if (raw.acao === 'set_filter') {
          const ctx = (vm._tourCtx && vm._tourCtx.stepsData && vm._tourCtx.stepsData.tour_context) || {};
          const delay = raw.timeout_ms || 2500;
          const speakPromise = vm._currentSpeakPromise || Promise.resolve();
          const fallbackPromise = new Promise(resolve => {
            this._autoClickTimeout = setTimeout(resolve, delay);
          });
          this._activeWaiterDriver = drv;
          Promise.all([speakPromise, fallbackPromise]).then(() => {
            if (vm._driverInstance !== drv || this._activeWaiterDriver !== drv) return;
            this._postSpeakTimeout = setTimeout(() => {
              if (vm._driverInstance !== drv) return;
              try { vm._applyTourFilters(ctx); } catch (e) { console.warn('[isaac-tour] set_filter falhou', e); }
              setTimeout(() => {
                if (vm._driverInstance !== drv) return;
                try { drv.moveNext(); } catch (_) {}
              }, 800);
            }, 800);
          });
        }

        // generate_report: clica em `#gerar` apos a narracao terminar e avanca o
        // tour quando `#relatorio` ficar visivel (ou apos `timeout_ms` como fallback).
        if (raw.acao === 'generate_report') {
          const timeoutMs = raw.timeout_ms || 12000;
          const speakPromise = vm._currentSpeakPromise || Promise.resolve();
          let advanced = false;
          const advance = () => {
            if (advanced) return;
            advanced = true;
            vm.detachStepWaiters();
            try { drv.moveNext(); } catch (_) {}
          };
          const tryAdvance = () => {
            const el = document.querySelector('#relatorio');
            if (el && el.offsetParent !== null) { advance(); return true; }
            return false;
          };
          this._activeWaiterDriver = drv;
          speakPromise.then(() => {
            if (vm._driverInstance !== drv || this._activeWaiterDriver !== drv) return;
            this._postSpeakTimeout = setTimeout(() => {
              if (vm._driverInstance !== drv) return;
              const gerar = document.querySelector('#gerar');
              if (gerar) { try { gerar.click(); } catch (_) {} }
              if (!tryAdvance()) {
                this._observer = new MutationObserver(() => { tryAdvance(); });
                this._observer.observe(document.body, { childList: true, subtree: true });
                this._waitTimeout = setTimeout(advance, timeoutMs);
              }
            }, 500);
          });
        }

        // navigate: salva estado, navega via Vue Router e o watcher de $route
        // retoma o tour após a nova rota montar.
        // Sincronizado com TTS: só navega quando a fala terminar E o timeout
        // estourar — evita cortar narração no meio.
        if (raw.acao === 'navigate' && raw.url_destino) {
          const speakPromise = vm._currentSpeakPromise || Promise.resolve();
          const minDelay = raw.timeout_ms || 1200;
          const fallbackPromise = new Promise(resolve => {
            this._navTimeout = setTimeout(resolve, minDelay);
          });
          this._activeWaiterDriver = drv;
          Promise.all([speakPromise, fallbackPromise]).then(() => {
            if (vm._driverInstance !== drv || this._activeWaiterDriver !== drv) return;
            this._postSpeakTimeout = setTimeout(() => {
              if (vm._driverInstance !== drv) return;
              const ctx = vm._tourCtx;
              if (ctx) {
                const resumeData = {
                  tourSlug: ctx.tourSlug,
                  apiBase: ctx.apiBase,
                  auth: ctx.auth,
                  isaacOrigin: ctx.isaacOrigin,
                  resumeIndex: index + 1,
                };
                try {
                  sessionStorage.setItem(TOUR_RESUME_KEY, JSON.stringify(resumeData));
                } catch (_) {}
              }
              // Flag avisa o onDestroyed pra NÃO apagar o TOUR_RESUME_KEY
              // que acabamos de salvar acima (race condition entre setItem e destroy).
              vm._navigatingToResume = true;
              try { drv.destroy(); } catch (_) {}
              try { vm.$router.push(raw.url_destino); } catch (e) {
                window.location.href = raw.url_destino;
              }
              vm._navigatingToResume = false;
            }, 800);
          });
        }
      },
      // ===== Helpers das acoes de cenario controlado =====
      // Setam estado da tela sem depender de cliques cegos do usuario. Os
      // selectors aqui sao acoplados ao Lancamentos.vue (relatorio gerencial)
      // mas o padrao serve para qualquer tela com um `#projeto` + datepickers.

      _applyTourSetProject(projectId, projectName) {
        // Retorna Promise: o `set_project` handler aguarda antes de avancar
        // o tour, pra dar tempo da `listaDeProjetosPermissao` (assincrona)
        // popular as options do select2 antes de tentar selecionar.
        //
        // Estrategia: PRIORIZA match por NOME (texto do option) porque o MM
        // ofusca IDs no HTML e o hash MUDA A CADA REQUEST. Salvar id fixo no
        // tour_context nao funciona. Match por texto e' estavel entre sessoes.
        // Se nao houver `projectName`, cai no fallback de match por value.
        return new Promise((resolve) => {
          if (projectId == null && !projectName) {
            console.warn('[isaac-tour] set_project: tour_context.demo_project_id e demo_project_name ausentes');
            resolve();
            return;
          }
          const $ = window.jQuery || window.$;
          const wantedName = (projectName || '').trim().toLowerCase();
          const findOption = (sel) => {
            if (!sel) return null;
            if (wantedName) {
              // Match por texto (case-insensitive, trim). Pega o primeiro option
              // cujo `.text` bate com `projectName`.
              for (const o of sel.options) {
                if ((o.text || '').trim().toLowerCase() === wantedName) return o;
              }
            }
            if (projectId != null) {
              return sel.querySelector('option[value="' + projectId + '"]') || null;
            }
            return null;
          };
          // Polling: espera ate 20s o option aparecer.
          // IMPORTANTE: re-busca `#projeto` a CADA tentativa. Vue pode substituir
          // o no (v-if/v-for em options reativas).
          const tryApply = (attempts) => {
            const sel = document.querySelector('#projeto');
            const opt = findOption(sel);
            if (!opt) {
              if (attempts >= 100) {
                console.warn(
                  '[isaac-tour] set_project: nenhum option bateu com name=' +
                  (projectName || '∅') + ' / id=' + (projectId || '∅') +
                  ' em #projeto apos 20s. Verifique se o projeto demo aparece ' +
                  'na lista de projetos do usuario para esse relatorio.'
                );
                resolve();
                return;
              }
              setTimeout(() => tryApply(attempts + 1), 200);
              return;
            }
            const realValue = opt.value;
            if ($ && $.fn && $.fn.select2 && $(sel).hasClass('select2-hidden-accessible')) {
              $(sel).val(String(realValue)).trigger('change');
            } else {
              sel.value = String(realValue);
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
            // Pausa pra Vue processar o change (recarregar rubricas, contas, etc).
            setTimeout(resolve, 600);
          };
          tryApply(0);
        });
      },

      _applyTourFilters(ctx) {
        if (!ctx) return;
        // Datas: vue-strap datepicker tem um <input> interno; setar value +
        // disparar input/change e suficiente para o v-model atualizar.
        const setDate = (sel, val) => {
          if (!val) return;
          const root = document.querySelector(sel);
          if (!root) return;
          const input = (root.tagName === 'INPUT') ? root : root.querySelector('input');
          if (!input) return;
          input.value = val;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        };
        setDate('#dataInicio', ctx.data_inicial);
        setDate('#dataFim', ctx.data_final);
        // Rendimento liquido: o checkbox so esta visivel quando o projeto tem
        // `exibirValorAplicRelFin` no MM. NAO usa <input type="checkbox"> nativo —
        // e' vue-strap <checkbox>, que renderiza so <label class="open checkbox
        // success [active]"> + <span class="icon">. Clicar no label dispara o
        // toggle interno do vue-strap. `active` na class indica que ja esta marcado.
        if (ctx.mostrar_rendimento_liquido) {
          const wraps = document.querySelectorAll('div.checkbox');
          for (const wrap of wraps) {
            const cbInput = wrap.querySelector('input[type="checkbox"]');
            if (cbInput) {
              if (!cbInput.checked) { try { cbInput.click(); } catch (_) {} }
              break;
            }
            // vue-strap: sem <input>; clica no <label> se ainda nao tem `active`.
            const label = wrap.querySelector('label.checkbox');
            if (label && !label.classList.contains('active')) {
              try { label.click(); } catch (_) {}
              break;
            }
          }
        }
      },

      detachStepWaiters() {
        if (this._clickTarget && this._clickHandler) {
          try { this._clickTarget.removeEventListener('click', this._clickHandler); } catch (_) {}
        }
        this._clickTarget = null;
        this._clickHandler = null;
        if (this._observer) {
          try { this._observer.disconnect(); } catch (_) {}
          this._observer = null;
        }
        if (this._waitTimeout) {
          clearTimeout(this._waitTimeout);
          this._waitTimeout = null;
        }
        if (this._navTimeout) {
          clearTimeout(this._navTimeout);
          this._navTimeout = null;
        }
        if (this._autoClickTimeout) {
          clearTimeout(this._autoClickTimeout);
          this._autoClickTimeout = null;
        }
        if (this._postSpeakTimeout) {
          clearTimeout(this._postSpeakTimeout);
          this._postSpeakTimeout = null;
        }
        this._activeWaiterDriver = null;
      },

      // ===== Preview do configurador do Isaac =====
      // Quando o configurador clica em "Pré-visualizar" no Isaac, ele abre o
      // MM em nova aba já apontando para a `url_inicial` do tour, com query
      // params `isaac_preview=<token>` e `isaac_origin=<origem do isaac>`.
      // O `apiBase` aponta para um endpoint dedicado de preview que aceita o
      // token assinado (validado no Isaac, ignora `ativo` para permitir testar
      // rascunhos). Token é tratado como o "tourSlug" para reusar startTour
      // sem alterar a forma da URL (`origin + apiBase + token + '/'`).
      tryStartIsaacPreview() {
        let params;
        try {
          params = new URLSearchParams(window.location.search);
        } catch (_) { return; }
        const token = params.get('isaac_preview');
        if (!token) return;

        const origin = params.get('isaac_origin') || this.isaacOrigin;
        if (!origin) {
          console.warn('[isaac-tour-preview] sem isaac_origin — ignorando.');
          return;
        }

        // Limpa os params da URL antes de iniciar — token expira em 5 min e
        // não deve sobreviver a refresh.
        try {
          params.delete('isaac_preview');
          params.delete('isaac_origin');
          const qs = params.toString();
          const cleanUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        } catch (_) {}

        // Autoplay policy do navegador: speechSynthesis.speak() na 1ª chamada
        // sem gesto do usuário na ABA do MM é silenciosamente bloqueado. O
        // gesto que abriu a aba (no Isaac) não conta — é outro document.
        // Mostra um overlay com botão "Iniciar" — o clique destrava o áudio
        // e dispara startTour. Sem isso o 1º step do tour não fala.
        this.showPreviewStartGate(token, origin);
      },

      showPreviewStartGate(token, origin) {
        // Remove instâncias anteriores (proteção contra re-trigger).
        const existing = document.getElementById('isaac-preview-gate');
        if (existing) existing.remove();

        const overlay = document.createElement('div');
        overlay.id = 'isaac-preview-gate';
        overlay.style.cssText =
          'position:fixed;inset:0;z-index:99999;background:rgba(15,23,42,0.55);' +
          'display:flex;align-items:center;justify-content:center;font-family:' +
          '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;';
        overlay.innerHTML =
          '<div style="background:#fff;border-radius:10px;padding:28px 32px;max-width:420px;' +
          'box-shadow:0 10px 40px rgba(0,0,0,0.3);text-align:center;">' +
            '<div style="font-size:32px;line-height:1;margin-bottom:8px;">🎬</div>' +
            '<h3 style="margin:0 0 6px;font-size:18px;color:#1f2937;font-weight:600;">' +
              'Pré-visualização pronta' +
            '</h3>' +
            '<p style="margin:0 0 18px;font-size:13px;color:#6b7280;line-height:1.5;">' +
              'Clique em <strong>Iniciar</strong> para começar a apresentação.<br>' +
              '<span style="color:#9ca3af;font-size:12px;">' +
                'A narração de voz precisa do seu clique para tocar.' +
              '</span>' +
            '</p>' +
            '<div>' +
              '<button type="button" id="isaac-preview-start-btn" ' +
              'style="background:#8b5cf6;color:#fff;border:0;padding:10px 28px;' +
              'border-radius:6px;font-size:14px;font-weight:500;cursor:pointer;">' +
                '▶ Iniciar apresentação' +
              '</button>' +
              '<button type="button" id="isaac-preview-cancel-btn" ' +
              'style="background:transparent;color:#6b7280;border:0;padding:10px 16px;' +
              'font-size:13px;cursor:pointer;margin-left:6px;">' +
                'Cancelar' +
              '</button>' +
            '</div>' +
          '</div>';
        document.body.appendChild(overlay);

        const cleanup = () => { try { overlay.remove(); } catch (_) {} };
        const startBtn = overlay.querySelector('#isaac-preview-start-btn');
        const cancelBtn = overlay.querySelector('#isaac-preview-cancel-btn');

        startBtn.focus();
        startBtn.addEventListener('click', () => {
          cleanup();
          // Pequeno delay pro Vue Router/DOM da rota inicial acomodar antes do
          // Driver.js procurar selectors.
          setTimeout(() => {
            this.startTour({
              tourSlug: token,
              apiBase: '/guias/preview/',
              auth: { mode: 'token', token: token },
              isaacOrigin: origin,
            });
          }, 200);
        });
        cancelBtn.addEventListener('click', cleanup);
      },

      // ===== Retomada após navigate =====
      tryResumePendingTour() {
        let raw;
        try {
          raw = sessionStorage.getItem(TOUR_RESUME_KEY);
        } catch (_) { return; }
        if (!raw) return;
        sessionStorage.removeItem(TOUR_RESUME_KEY);
        let payload;
        try {
          payload = JSON.parse(raw);
        } catch (_) { return; }
        // Aguarda o DOM da nova rota acomodar.
        setTimeout(() => {
          this.startTour({
            tourSlug: payload.tourSlug,
            apiBase: payload.apiBase,
            auth: payload.auth,
            isaacOrigin: payload.isaacOrigin,
            _resumeIndex: payload.resumeIndex,
          });
        }, 600);
      },

      // ===== Stop / cleanup =====
      stopTour() {
        if (this._driverInstance) {
          try { this._driverInstance.destroy(); } catch (_) {}
          this._driverInstance = null;
        }
        this.detachStepWaiters();
        this.stopSpeak();
        this.tourActive = false;
        try { sessionStorage.removeItem(TOUR_RESUME_KEY); } catch (_) {}
      },

      // ===== TTS (Web Speech API) =====
      // Seleção de voz: a default do navegador para pt-BR costuma ser robótica
      // (Microsoft Maria offline no Windows, eSpeak no Linux). As neurais e as
      // do Google ficam MUITO melhores. Match por substring contra a lista
      // TTS_VOICE_PREFERENCE (top-level), mais natural primeiro.
      _pickBestPtBrVoice() {
        if (!('speechSynthesis' in window)) return null;
        const all = window.speechSynthesis.getVoices() || [];
        if (!all.length) return null;
        const ptBr = all.filter(v => (v.lang || '').toLowerCase().startsWith('pt'));
        if (!ptBr.length) return null;
        for (const pref of TTS_VOICE_PREFERENCE) {
          const hit = ptBr.find(v => (v.name || '').toLowerCase().includes(pref));
          if (hit) return hit;
        }
        // Sem preferência batendo: prioriza pt-BR sobre pt-PT, depois 1ª.
        const brOnly = ptBr.filter(v => (v.lang || '').toLowerCase() === 'pt-br');
        return brOnly[0] || ptBr[0];
      },
      // Retorna Promise que resolve quando a TTS termina (ou imediatamente se
      // estiver mutado/sem suporte). Os handlers de auto_click/wait_for usam
      // isso pra só avançar APÓS a narração terminar — evita cortar fala.
      // [CHATBOT-WIDGET] Assinatura: speak(audioUrl, fallbackText).
      //   - Se audioUrl presente: toca MP3 pre-renderizado (TTS humano do Isaac).
      //   - Caso contrario: usa speechSynthesis com o texto (qualidade do navegador).
      speak(audioUrl, fallbackText) {
        if (this.tourMuted) return Promise.resolve();

        // Caminho preferido: audio pre-renderizado do Isaac (voz mais humana).
        if (audioUrl) {
          return new Promise(resolve => {
            try {
              this._stopCurrentAudio();
              const audio = new Audio(audioUrl);
              audio.preload = 'auto';
              this._currentAudio = audio;
              const done = () => {
                if (this._currentAudio === audio) this._currentAudio = null;
                resolve();
              };
              audio.addEventListener('ended', done, { once: true });
              audio.addEventListener('error', done, { once: true });
              audio.play().catch(() => {
                // Autoplay bloqueado ou erro de play: cai pro speechSynthesis.
                this._currentAudio = null;
                this._speakViaSynthesis(fallbackText).then(resolve);
              });
              // Fallback duro: nunca segura mais que 120s.
              setTimeout(done, 120000);
            } catch (_) {
              this._speakViaSynthesis(fallbackText).then(resolve);
            }
          });
        }

        // Sem audio_url -> speechSynthesis (comportamento antigo).
        return this._speakViaSynthesis(fallbackText);
      },
      _speakViaSynthesis(text) {
        if (!text) return Promise.resolve();
        if (!('speechSynthesis' in window)) return Promise.resolve();
        return new Promise(resolve => {
          try {
            window.speechSynthesis.cancel();
            const utt = new SpeechSynthesisUtterance(this.stripHtml(text));
            utt.lang = 'pt-BR';
            utt.rate = 1.0;
            utt.pitch = 1.0;
            if (!this._chosenVoice) {
              this._chosenVoice = this._pickBestPtBrVoice();
            }
            if (this._chosenVoice) {
              utt.voice = this._chosenVoice;
            }
            utt.onend = () => resolve();
            utt.onerror = () => resolve();
            window.speechSynthesis.speak(utt);
            // Fallback: se nem onend nem onerror disparar em 60s, libera.
            setTimeout(resolve, 60000);
          } catch (_) {
            resolve();
          }
        });
      },
      _stopCurrentAudio() {
        if (this._currentAudio) {
          try {
            this._currentAudio.pause();
            this._currentAudio.currentTime = 0;
          } catch (_) {}
          this._currentAudio = null;
        }
      },
      stopSpeak() {
        try {
          if ('speechSynthesis' in window) window.speechSynthesis.cancel();
        } catch (_) {}
        this._stopCurrentAudio();
      },
      toggleMute() {
        this.tourMuted = !this.tourMuted;
        try { localStorage.setItem(TOUR_MUTED_KEY, this.tourMuted ? '1' : '0'); } catch (_) {}
        if (this.tourMuted) this.stopSpeak();
      },

      // ===== Telemetria =====
      reportEvent(eventName, stepIndex) {
        const ctx = this._tourCtx;
        if (!ctx) return;
        const url = ctx.isaacOrigin + ctx.apiBase + ctx.tourSlug + '/event/';
        const body = JSON.stringify({ event: eventName, step_index: stepIndex, ts: Date.now() });
        try {
          // Uso de fetch keepalive para sobreviver à navegação na página.
          fetch(url, {
            method: 'POST',
            credentials: (ctx.auth && ctx.auth.mode === 'session') ? 'include' : 'omit',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => {});
        } catch (_) { /* noop */ }
      },

      stripHtml(html) {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = String(html);
        return (tmp.textContent || tmp.innerText || '').trim();
      },
    }
  }
</script>

<template>
  <div v-if="isVisible && !tourActive" class="hidden-print chatbot-widget-root">
    <!-- Botão fechado: position absolute, acompanha o scroll (junto do footer) -->
    <button
      v-show="!isOpen"
      type="button"
      class="chatbot-widget-fab-closed"
      @click="toggle"
      title="Assistente Virtual">
      <i class="fa fa-comments" style="font-size:14px;"></i>
    </button>

    <!-- Chat aberto: botão X acima + janela abaixo -->
    <div v-show="isOpen" class="chatbot-widget-open-container">
      <!-- Botão X flutuante acima da janela -->
      <button
        type="button"
        class="chatbot-widget-fab-open"
        @click="close"
        title="Fechar chat">
        <i class="fa fa-times" style="font-size:16px;"></i>
      </button>

      <!-- Janela do chat -->
      <div class="chatbot-widget-window">
        <div class="chatbot-widget-header">
          <span>
            <i class="fa fa-comments" style="margin-right:8px;"></i>
            Assistente Virtual
          </span>
        </div>
        <iframe
          v-if="iframeLoaded"
          ref="isaacIframe"
          :src="isaacHubUrl"
          class="chatbot-widget-iframe"
          allow="clipboard-write; microphone"
          frameborder="0"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<style>
  .chatbot-widget-root {
    position: static;
    width: 0;
    height: 0;
    overflow: visible;
  }

  /* Fechado: absolute no page-wrapper, acompanha o scroll com o footer */
  .chatbot-widget-fab-closed {
    position: absolute;
    bottom: 15px;
    right: 44px;
    z-index: 999;
    cursor: pointer;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    background: #1ab394;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;
  }

  .chatbot-widget-fab-closed:hover {
    background: #18a689;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    transform: scale(1.1);
  }

  .chatbot-widget-fab-closed:focus {
    outline: none;
  }

  /* Container fixo quando aberto */
  .chatbot-widget-open-container {
    position: fixed;
    bottom: 15px;
    right: 15px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
  }

  /* Botão X acima da janela */
  .chatbot-widget-fab-open {
    cursor: pointer;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: #ed5565;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .chatbot-widget-fab-open:hover {
    background: #da4453;
    transform: scale(1.1);
  }

  .chatbot-widget-fab-open:focus {
    outline: none;
  }

  /* Janela do chat */
  .chatbot-widget-window {
    width: 793px;
    height: calc(100vh - 125px);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    background: #fff;
  }

  .chatbot-widget-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    background: #1ab394;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .chatbot-widget-iframe {
    flex: 1;
    width: 100%;
    border: none;
  }

  @media (max-width: 850px) {
    .chatbot-widget-window {
      width: calc(100vw - 30px);
    }
  }

  @media (max-width: 480px) {
    .chatbot-widget-open-container {
      bottom: 10px;
      right: 10px;
    }
    .chatbot-widget-window {
      width: calc(100vw - 20px);
      height: calc(100vh - 80px);
    }
  }

  @media print {
    .chatbot-widget-fab-closed,
    .chatbot-widget-open-container {
      display: none !important;
    }
  }

  /* === Driver.js: ajustes leves para integrar com a paleta do MM === */
  .driver-popover-mute-btn {
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0 6px;
    font-size: 16px;
    line-height: 1;
    float: right;
  }
  .driver-popover-mute-btn:focus {
    outline: 1px dashed #1ab394;
  }
  .driver-popover.isaac-tour-navigate .driver-popover-title::after {
    content: '  →';
    color: #1ab394;
    font-weight: 700;
  }
</style>



// WEBPACK FOOTER //
// FrontEnd/componentes/ChatBotWidget.vue